import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Library,
  Search,
  Loader2,
  Save,
  RefreshCcw,
  X,
  ChevronDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";
import { companyTypeLabel } from "@/lib/companyTypes";

const SUB_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "GST", label: "GST" },
  { key: "Income Tax", label: "Income Tax" },
  { key: "ROC", label: "ROC / MCA" },
  { key: "Labour Law", label: "Labour" },
  { key: "FEMA", label: "FEMA" },
  { key: "DGFT", label: "DGFT" },
];

/**
 * MasterLibraryModal — Rules × Clients matrix with per-cell overrides.
 *
 * `pendingChanges` holds { [clientId]: { [code]: enabled } } that the user has
 * edited but not saved. On Save we send one PUT per client (batched).
 */
export default function MasterLibraryModal({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["master-matrix"],
    queryFn: api.masterMatrix,
    enabled: open,
    refetchOnMount: true,
  });

  const [subCat, setSubCat] = useState("all");
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState({}); // { clientId: { code: enabled } }
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenMonths, setRegenMonths] = useState(12);

  useEffect(() => {
    if (open) {
      setPending({});
      setSubCat("all");
      setSearch("");
    }
  }, [open]);

  const rules = data?.rules || [];
  const clients = data?.clients || [];

  const filteredRules = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rules.filter((r) => {
      if (subCat !== "all" && r.subCategory !== subCat) return false;
      if (term) {
        const hay = `${r.title} ${r.form || ""} ${r.code}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rules, subCat, search]);

  const isEffective = (clientId, code) => {
    const p = pending[clientId]?.[code];
    if (p !== undefined) return p;
    return (data?.effective?.[clientId] || []).includes(code);
  };

  const isDefault = (rule, clientType) =>
    (rule.applicableTo || []).includes(clientType);

  const cellChanged = (clientId, code) => pending[clientId]?.[code] !== undefined;

  const totalChanges = useMemo(
    () =>
      Object.values(pending).reduce(
        (acc, byCode) => acc + Object.keys(byCode).length,
        0
      ),
    [pending]
  );

  const toggle = (clientId, code) => {
    setPending((prev) => {
      const cur = prev[clientId] || {};
      const currentValue = cur[code];
      const persisted = (data?.effective?.[clientId] || []).includes(code);
      const nextEffective =
        currentValue === undefined ? !persisted : !currentValue;
      const client = clients.find((c) => c.id === clientId);
      const ruleDefault = isDefault(
        rules.find((r) => r.code === code),
        client?.type
      );
      const newCur = { ...cur };
      // If the new state matches the default, drop the pending entry (net no-op).
      if (nextEffective === ruleDefault && currentValue !== undefined) {
        delete newCur[code];
      } else if (nextEffective === persisted) {
        // Reverted to persisted state before save; drop pending entry.
        delete newCur[code];
      } else {
        newCur[code] = nextEffective;
      }
      const next = { ...prev };
      if (Object.keys(newCur).length === 0) delete next[clientId];
      else next[clientId] = newCur;
      return next;
    });
  };

  const saveM = useMutation({
    mutationFn: async () => {
      const results = [];
      for (const [clientId, byCode] of Object.entries(pending)) {
        const changes = Object.entries(byCode).map(([code, enabled]) => ({
          code,
          enabled,
        }));
        if (changes.length === 0) continue;
        const r = await api.updateClientRules(clientId, changes);
        results.push(r);
      }
      return results;
    },
    onSuccess: () => {
      toast.success(`Saved ${totalChanges} change${totalChanges === 1 ? "" : "s"}`);
      qc.invalidateQueries({ queryKey: ["master-matrix"] });
      setPending({});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const regenM = useMutation({
    mutationFn: async () => {
      const affectedIds = new Set(Object.keys(pending));
      // If nothing pending, regenerate ALL clients so they align to library.
      const targets =
        affectedIds.size > 0
          ? clients.filter((c) => affectedIds.has(c.id))
          : clients;
      const results = [];
      for (const c of targets) {
        const r = await api.regenerateClientRules(c.id, {
          months: Number(regenMonths),
          removeDisabled: true,
        });
        results.push({ name: c.name, ...r });
      }
      return results;
    },
    onSuccess: (results) => {
      const created = results.reduce((a, r) => a + (r.createdCount || 0), 0);
      const removed = results.reduce((a, r) => a + (r.removedCount || 0), 0);
      toast.success(
        `Regenerated ${results.length} client${results.length === 1 ? "" : "s"} · +${created} · −${removed}`
      );
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["master-matrix"] });
      setRegenOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Regenerate failed"),
  });

  const disabled = saveM.isPending || regenM.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !disabled && onOpenChange(o)}>
      <DialogContent
        className="p-0 gap-0 border-[#E2E8F0] max-w-[95vw] w-[1400px] max-h-[90vh] flex flex-col"
        data-testid={TID.libraryModal}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E8F0] shrink-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <DialogTitle className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
                <Library className="h-4 w-4 text-[#64748B]" />
                Master Compliance Library
              </DialogTitle>
              <DialogDescription className="text-xs text-[#64748B]">
                Preview all statutory compliances and toggle which apply to each
                client. Save persists overrides; Regenerate rewrites the client's
                task list based on the effective selections.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {totalChanges > 0 && (
                <span
                  className="text-[11px] font-medium bg-[#FEF3C7] text-[#92400E] rounded px-2 py-0.5"
                  data-testid={TID.libraryChangesBadge}
                >
                  {totalChanges} unsaved change{totalChanges === 1 ? "" : "s"}
                </span>
              )}
              <button
                type="button"
                onClick={() => saveM.mutate()}
                disabled={disabled || totalChanges === 0}
                data-testid={TID.librarySave}
                className="h-8 px-3 rounded-md bg-[#0F172A] text-white text-xs font-medium hover:bg-[#1E293B] disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {saveM.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Save
              </button>
              <button
                type="button"
                onClick={() => setRegenOpen(true)}
                disabled={disabled || clients.length === 0}
                data-testid={TID.libraryRegenerate}
                className="h-8 px-3 rounded-md border border-[#E2E8F0] bg-white text-xs font-medium text-[#334155] hover:bg-[#F8FAFC] disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <RefreshCcw className="h-3.5 w-3.5" />
                Regenerate…
              </button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={disabled}
                data-testid={TID.libraryClose}
                className="h-8 w-8 rounded-md border border-[#E2E8F0] bg-white text-[#334155] hover:bg-[#F8FAFC] inline-flex items-center justify-center disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="inline-flex items-center gap-1 p-0.5 bg-[#F1F5F9] rounded-lg">
              {SUB_CATEGORIES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSubCat(s.key)}
                  data-testid={TID.libraryTab(s.key)}
                  className={cn(
                    "h-7 px-2.5 text-[11px] font-medium rounded-md",
                    subCat === s.key
                      ? "bg-white text-[#0F172A] border border-[#E2E8F0] shadow-sm"
                      : "text-[#64748B] hover:text-[#0F172A]"
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by rule title, form or code…"
                className="w-full h-7 pl-8 pr-2 rounded-md border border-[#E2E8F0] bg-white text-xs placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10"
                data-testid={TID.librarySearch}
              />
            </div>
            <div className="text-[11px] text-[#64748B]">
              {filteredRules.length} of {rules.length} rules · {clients.length}{" "}
              client{clients.length === 1 ? "" : "s"}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="p-6 text-xs text-[#64748B]">Loading library…</div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#64748B]">
              Add at least one client to configure their compliance library.
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#64748B]">
              No rules match the current filters.
            </div>
          ) : (
            <table className="w-full text-left border-separate border-spacing-0">
              <thead className="sticky top-0 z-20 bg-white">
                <tr>
                  <th
                    className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium px-3 py-2 border-b border-[#E2E8F0] bg-white sticky left-0 z-30"
                    style={{ minWidth: 340 }}
                  >
                    Compliance Rule
                  </th>
                  {clients.map((c) => (
                    <th
                      key={c.id}
                      className="text-center px-2 py-2 border-b border-[#E2E8F0] bg-white"
                      style={{ minWidth: 120 }}
                    >
                      <div
                        className="text-[11px] font-semibold text-[#0F172A] truncate max-w-[130px] mx-auto"
                        title={c.name}
                      >
                        {c.name}
                      </div>
                      <div className="text-[10px] text-[#64748B] truncate max-w-[130px] mx-auto">
                        {companyTypeLabel(c.type)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRules.map((r) => (
                  <tr
                    key={r.code}
                    data-testid={TID.libraryRuleRow(r.code)}
                    className="hover:bg-[#F8FAFC]"
                  >
                    <td className="px-3 py-2 border-b border-[#E2E8F0] bg-white sticky left-0 z-10 align-top">
                      <div className="flex items-start gap-2">
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full mt-1.5 shrink-0"
                          style={{
                            background:
                              r.category === "Tax" ? "#0F172A" : "#F59E0B",
                          }}
                          aria-hidden
                        />
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-[#0F172A] leading-tight">
                            {r.title}
                          </div>
                          <div className="text-[10px] text-[#64748B] mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span className="bg-[#F1F5F9] rounded px-1 py-0.5 text-[#334155] font-medium">
                              {r.subCategory}
                            </span>
                            {r.form && (
                              <span className="text-[#94A3B8]">· {r.form}</span>
                            )}
                            <span className="text-[#94A3B8]">
                              · Day {r.dueDay}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    {clients.map((c) => {
                      const eff = isEffective(c.id, r.code);
                      const def = isDefault(r, c.type);
                      const changed = cellChanged(c.id, r.code);
                      const override = eff !== def; // saved OR pending diff from default
                      return (
                        <td
                          key={c.id}
                          className="text-center border-b border-[#E2E8F0] align-middle p-0"
                        >
                          <button
                            type="button"
                            onClick={() => toggle(c.id, r.code)}
                            data-testid={TID.libraryCell(c.id, r.code)}
                            title={
                              def
                                ? "Default: applies to this type"
                                : "Default: opt-in for this type"
                            }
                            className="h-8 w-full inline-flex items-center justify-center relative group"
                          >
                            <span
                              className={cn(
                                "h-4 w-4 rounded border inline-flex items-center justify-center transition-colors",
                                eff
                                  ? "bg-[#0F172A] border-[#0F172A]"
                                  : "bg-white border-[#CBD5E1] group-hover:border-[#94A3B8]"
                              )}
                            >
                              {eff && (
                                <svg
                                  viewBox="0 0 12 12"
                                  className="h-2.5 w-2.5 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                >
                                  <path d="M2 6l3 3 5-6" />
                                </svg>
                              )}
                            </span>
                            {(override || changed) && (
                              <span
                                className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full"
                                style={{
                                  background: changed ? "#F59E0B" : "#0F172A",
                                }}
                                title={
                                  changed ? "Unsaved override" : "Custom override"
                                }
                              />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-2 border-t border-[#E2E8F0] bg-[#F8F9FA] text-[11px] text-[#64748B] flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border bg-[#0F172A] border-[#0F172A] inline-block" />
            Applies to client
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border bg-white border-[#CBD5E1] inline-block" />
            Not applied
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#0F172A] inline-block" />
            Custom override
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B] inline-block" />
            Unsaved
          </span>
        </div>
      </DialogContent>

      <RegenerateDialog
        open={regenOpen}
        onOpenChange={(o) => !regenM.isPending && setRegenOpen(o)}
        months={regenMonths}
        onMonthsChange={setRegenMonths}
        onConfirm={() => regenM.mutate()}
        submitting={regenM.isPending}
        pendingClients={Object.keys(pending).length}
        totalClients={clients.length}
      />
    </Dialog>
  );
}

function RegenerateDialog({
  open,
  onOpenChange,
  months,
  onMonthsChange,
  onConfirm,
  submitting,
  pendingClients,
  totalClients,
}) {
  const scope = pendingClients > 0 ? pendingClients : totalClients;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 border-[#E2E8F0]">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E8F0]">
          <DialogTitle className="text-base font-semibold text-[#0F172A]">
            Regenerate compliance tasks
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Rewrites the compliance task list for {scope} client
            {scope === 1 ? "" : "s"}. New rules add upcoming tasks; disabled
            rules remove Pending tasks (Completed history is preserved).
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 py-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#334155]">
              Horizon (months)
            </span>
            <input
              type="number"
              min={1}
              max={36}
              value={months}
              onChange={(e) => onMonthsChange(e.target.value)}
              className="w-24 h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
            />
          </label>
          <div className="text-[11px] text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-2">
            {pendingClients > 0
              ? `Only the ${pendingClients} client${pendingClients === 1 ? "" : "s"} with unsaved changes will be affected.`
              : `All ${totalClients} client${totalClients === 1 ? "" : "s"} will be re-synced with the current library.`}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] flex items-center justify-end gap-2 rounded-b-lg">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#334155] hover:bg-[#F1F5F9] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="h-9 px-3.5 rounded-md bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Regenerate
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
